import {
  Component,
  OnInit,
  Inject,
  ChangeDetectorRef,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EquipmentService } from '@app/services/equipment.service';
import {
  Equipment,
  EquipmentMake,
  EquipmentModel,
  EquipmentMastOption,
  EquipmentImage,
  EQUIPMENT_FUEL_TYPES,
  EQUIPMENT_ORIGINS,
  buildProductionDate,
  parseProductionDate
} from '@app/model/equipment';
import { environment } from 'environments/environment';
import { ToastrService } from 'ngx-toastr';
interface DialogData {
  make?: string;
  model?: string;
  equipment?: Equipment;
}
@Component({
  selector: 'app-add-equipment-dialog',
  templateUrl: './add-equipment-dialog.component.html',
  styleUrls: ['./add-equipment-dialog.component.scss']
})
export class AddEquipmentDialogComponent implements OnInit, OnDestroy {
  equipmentForm: FormGroup;
  allMakes: EquipmentMake[] = [];
  filteredMakes: string[] = [];
  filteredModels: EquipmentModel[] = [];
  availableMastOptions: EquipmentMastOption[] = [];
  selectedMake: EquipmentMake | null = null;
  selectedModel: EquipmentModel | null = null;
  filteredPrefixes: any[] = [];
  filteredPrefixesFiltered: any[] = [];
  selectedPrefix: any = null;
  prefixSearch = '';
  selectedMast: string = '';
  makeSearch = '';
  modelSearch = '';
  isLoadingMakes = true;
  isLoadingModels = false;
  isLoadingMoreModels = false;
  isSaving = false;
  modelsPage = 1;
  modelsPageSize = 10;
  hasMoreModels = true;
  brandsPage = 1;
  brandsPageSize = 50;
  hasMoreBrands = true;
  isLoadingMoreBrands = false;
  isEdit = false;
  equipmentId = '';
  selectedFiles: File[] = [];
  imagePreviews: string[] = [];
  existingImages: EquipmentImage[] = [];
  isUploadingImages = false;
  months: number[] = Array.from({ length: 12 }, (_, i) => i + 1);
  fuelTypeOptions: string[] = [...EQUIPMENT_FUEL_TYPES];
  originOptions: string[] = [...EQUIPMENT_ORIGINS];
  readonly FUEL_OTHER = '__other__';
  private makeSearchDebounceTimer: any = null;
  private modelSearchDebounceTimer: any = null;
  private modelSubscription: Subscription = null;
  activeBrandIds: Set<string> = new Set();
  private subs: Subscription[] = [];
  @ViewChild('dialogBody', { static: false }) dialogBodyRef: ElementRef;
  constructor(
    public dialogRef: MatDialogRef<AddEquipmentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData | null,
    private fb: FormBuilder,
    private equipmentService: EquipmentService,
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService
  ) {}
  ngOnInit(): void {
    let ownReference = '';
    let location = '';
    let productionYear: string | null = null;
    let productionMonth: number | null = null;
    let make = '';
    let model = '';
    let serialPrefix = '';
    let serialNumber = '';
    let equipmentModelPrefixId = '';
    let licensePlate = '';
    let hourMeter = 0;
    let notes = '';
    let chassisSerialNumber = '';
    let engineBrand = '';
    let engineModel = '';
    let engineSerialNumber = '';
    let transmissionBrand = '';
    let transmissionModel = '';
    let transmissionSerialNumber = '';
    let axleBrand = '';
    let axleModel = '';
    let axleSerialNumber = '';
    let mastType = '';
    let mastModel = '';
    let mastSerialNumber = '';
    let origin = '';
    let fuelType = '';
    let fuelTypeOther = '';
    let capacity = '';
    let steeringTireSize = '';
    let steeringTireType = '';
    let loadTireSize = '';
    let loadTireType = '';
    let spareTireSize = '';
    let spareTireType = '';
    let status = 'active';
    if (this.data && this.data.equipment) {
      this.isEdit = true;
      const eq = this.data.equipment;
      this.equipmentId = eq.id;
      ownReference = eq.ownReference || '';
      status = eq.status || 'active';
      location = eq.location || '';
      const parsedProduction = parseProductionDate(eq.productionYear);
      productionMonth = parsedProduction.month;
      productionYear = parsedProduction.year || null;
      if (eq.origin && this.originOptions.indexOf(eq.origin) === -1) {
        this.originOptions = [...this.originOptions, eq.origin];
      }
      if (eq.fuelType && this.fuelTypeOptions.indexOf(eq.fuelType) === -1) {
        fuelTypeOther = eq.fuelType;
        fuelType = this.FUEL_OTHER;
      }
      make = eq.make || '';
      model = eq.model || '';
      const parts = (eq.serialNumber || '').split('-');
      if (parts.length > 1) {
        serialPrefix = parts[0];
        serialNumber = parts.slice(1).join('-');
      } else {
        serialPrefix = '';
        serialNumber = eq.serialNumber || '';
      }
      licensePlate = eq.licensePlate || '';
      equipmentModelPrefixId = eq.equipmentModelPrefixId || '';
      hourMeter = eq.hourMeter || 0;
      notes = eq.notes || '';
      chassisSerialNumber = eq.chassisSerialNumber || '';
      engineBrand = eq.engineBrand || '';
      engineModel = eq.engineModel || '';
      engineSerialNumber = eq.engineSerialNumber || '';
      transmissionBrand = eq.transmissionBrand || '';
      transmissionModel = eq.transmissionModel || '';
      transmissionSerialNumber = eq.transmissionSerialNumber || '';
      axleBrand = eq.axleBrand || '';
      axleModel = eq.axleModel || '';
      axleSerialNumber = eq.axleSerialNumber || '';
      mastType = eq.mastType || '';
      mastModel = eq.mastModel || '';
      mastSerialNumber = eq.mastSerialNumber || '';
      origin = eq.origin || '';
      if (eq.fuelType && this.fuelTypeOptions.indexOf(eq.fuelType) !== -1) {
        fuelType = eq.fuelType;
      }
      capacity = eq.capacity || '';
      steeringTireSize = eq.steeringTireSize || '';
      steeringTireType = eq.steeringTireType || '';
      loadTireSize = eq.loadTireSize || '';
      loadTireType = eq.loadTireType || '';
      spareTireSize = eq.spareTireSize || '';
      spareTireType = eq.spareTireType || '';
    }
    this.equipmentForm = this.fb.group({
      ownReference: [ownReference],
      location: [location],
      productionMonth: [productionMonth],
      productionYear: [productionYear],
      make: [make, Validators.required],
      model: [model, Validators.required],
      equipmentModelPrefixId: [equipmentModelPrefixId],
      serialPrefix: [serialPrefix],
      serialNumber: [serialNumber],
      licensePlate: [licensePlate],
      hourMeter: [hourMeter, [Validators.min(0)]],
      notes: [notes],
      chassisSerialNumber: [chassisSerialNumber],
      engineBrand: [engineBrand],
      engineModel: [engineModel],
      engineSerialNumber: [engineSerialNumber],
      transmissionBrand: [transmissionBrand],
      transmissionModel: [transmissionModel],
      transmissionSerialNumber: [transmissionSerialNumber],
      axleBrand: [axleBrand],
      axleModel: [axleModel],
      axleSerialNumber: [axleSerialNumber],
      mastType: [mastType],
      mastModel: [mastModel],
      mastSerialNumber: [mastSerialNumber],
      status: [status],
      origin: [origin],
      fuelType: [fuelType],
      fuelTypeOther: [fuelTypeOther],
      capacity: [capacity],
      // Tire information
      steeringTireSize: [steeringTireSize],
      steeringTireType: [steeringTireType],
      loadTireSize: [loadTireSize],
      loadTireType: [loadTireType],
      spareTireSize: [spareTireSize],
      spareTireType: [spareTireType]
    });
    const sub = this.equipmentService.getActiveBrandIds().subscribe((ids) => {
      this.activeBrandIds = ids;
      this.loadBrands();
    });
    this.subs.push(sub);

    if (this.isEdit && this.equipmentId && this.data && this.data.equipment) {
      this.existingImages = this.data.equipment.extendImages || [];
    }
  }

  loadExistingImages(): void {
    if (this.data && this.data.equipment && this.data.equipment.extendImages) {
      this.existingImages = this.data.equipment.extendImages;
    } else {
      this.existingImages = [];
    }
    this.cdr.detectChanges();
  }

  getFullImagePath(path: string): string {
    if (!path) return '/assets/media/products/placeholder.png';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('assets/')) {
      return path;
    }
    const host = environment.backendhost || '';
    const cleanHost = host.endsWith('/') ? host.slice(0, -1) : host;
    const cleanPath = path.startsWith('/') ? path : '/' + path;
    return cleanHost + cleanPath;
  }

  onImagesSelected(event: any): void {
    const files: File[] = Array.from(event.target.files);
    if (!files || files.length === 0) return;
    for (const file of files) {
      this.selectedFiles.push(file);
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imagePreviews.push(e.target.result);
        this.cdr.detectChanges();
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  }

  removeNewImage(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.imagePreviews.splice(index, 1);
    this.cdr.detectChanges();
  }

  removeExistingImage(imageId: string, event: Event): void {
    event.stopPropagation();
    if (!imageId) return;
    const sub = this.equipmentService.deleteEquipmentImage(imageId).subscribe(
      () => {
        this.existingImages = this.existingImages.filter((img) => img.id !== imageId);
        this.toastr.success('Đã xóa ảnh');
        this.cdr.detectChanges();
      },
      (err) => {
        console.error('Lỗi xóa ảnh:', err);
        this.toastr.error('Không thể xóa ảnh');
      }
    );
    this.subs.push(sub);
  }
  ngOnDestroy(): void {
    if (this.makeSearchDebounceTimer) {
      clearTimeout(this.makeSearchDebounceTimer);
    }

    if (this.modelSearchDebounceTimer) {
      clearTimeout(this.modelSearchDebounceTimer);
    }

    if (this.modelSubscription) {
      this.modelSubscription.unsubscribe();
    }

    this.subs.forEach((subscription) => subscription.unsubscribe());
  }
  loadBrands(): void {
    this.brandsPage = 1;
    this.isLoadingMakes = true;
    this.cdr.detectChanges();
    this.fetchBrandsPage(this.brandsPage, this.brandsPageSize, '', false);
  }

  private fetchBrandsPage(page: number, pageSize: number, search: string, append: boolean): void {
    const sub = this.equipmentService.getBrands(page, pageSize, search).subscribe((res) => {
      const filtered = res.brands.filter((b) => this.activeBrandIds.has(b.id));
      if (append) {
        this.allMakes = [...this.allMakes, ...filtered];
      } else {
        this.allMakes = filtered;
      }
      this.filteredMakes = this.allMakes.map((m) => m.name);
      this.hasMoreBrands = res.hasMore;
      this.isLoadingMakes = false;
      this.isLoadingMoreBrands = false;
      // Auto-load next page only if current page has 0 visible brands
      if (filtered.length === 0 && res.hasMore) {
        this.isLoadingMoreBrands = true;
        this.brandsPage = page + 1;
        this.fetchBrandsPage(this.brandsPage, pageSize, search, append);
        return;
      }
      if (!append) {
        if (this.isEdit && this.data && this.data.equipment) {
          const eq = this.data.equipment;
          const savedPrefixId = eq.equipmentModelPrefixId || '';
          if (eq.make) {
            this.selectMake(eq.make);
            if (!this.selectedMake) {
              this.makeSearch = eq.make;
              this.equipmentForm.patchValue({ make: eq.make });
            }
            if (eq.model) {
              const modelObj = this.selectedMake
                ? this.selectedMake.models.find((m) => m.model === eq.model)
                : null;
              if (modelObj) {
                this.equipmentForm.patchValue(
                  { equipmentModelPrefixId: savedPrefixId },
                  { emitEvent: false }
                );
                this.selectModel(modelObj);
              } else {
                this.selectedModel = {
                  id: eq.equipmentModelId || '',
                  brandId: eq.brandId || '',
                  model: eq.model,
                  startDate: ''
                };
                this.equipmentForm.patchValue(
                  { model: eq.model, equipmentModelPrefixId: savedPrefixId },
                  { emitEvent: false }
                );
                this.loadModelPrefixes(eq.equipmentModelId || '');
              }
            }
            if (eq.mastType) {
              const mastCode = eq.mastType.split(' - ')[0];
              const mastOpt = this.availableMastOptions.find((m) => m.code === mastCode);
              if (mastOpt) {
                this.selectedMast = mastOpt.code;
              } else {
                this.selectedMast = mastCode;
              }
            }
          }
        } else if (this.data && this.data.make) {
          this.selectMake(this.data.make);
          if (this.data.model) {
            const modelObj = this.selectedMake
              ? this.selectedMake.models.find((m) => m.model === this.data.model)
              : null;
            if (modelObj) {
              this.selectModel(modelObj);
            }
          }
        }
      }
      this.cdr.detectChanges();
    });
    this.subs.push(sub);
  }
  onMakeScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom && !this.isLoadingMoreBrands && this.hasMoreBrands) {
      this.isLoadingMoreBrands = true;
      this.brandsPage++;
      this.fetchBrandsPage(this.brandsPage, this.brandsPageSize, this.makeSearch, true);
    }
  }
  onMakeSearch(value: string): void {
    this.makeSearch = value;
    if (this.makeSearchDebounceTimer) {
      clearTimeout(this.makeSearchDebounceTimer);
    }
    this.makeSearchDebounceTimer = setTimeout(() => {
      this.brandsPage = 1;
      this.isLoadingMakes = true;
      this.cdr.detectChanges();
      this.fetchBrandsPage(this.brandsPage, this.brandsPageSize, this.makeSearch, false);
    }, 300);
  }
  selectMake(makeName: string): void {
    const savedScrollTop = this.dialogBodyRef ? this.dialogBodyRef.nativeElement.scrollTop : 0;
    const make = this.allMakes.find((m) => m.name === makeName);
    if (make) {
      this.selectedMake = make;
      this.equipmentForm.patchValue({ make: makeName });
      this.availableMastOptions = make.mastOptions;
      this.selectedModel = null;
      this.modelSearch = '';
      this.filteredPrefixes = [];
      this.selectedMast = '';
      this.equipmentForm.patchValue({ model: '', equipmentModelPrefixId: '' });
      // Reset pagination state
      this.modelsPage = 1;
      this.hasMoreModels = true;
      // Load models filtered by brand_id from API (client-side filter + server pagination)
      if (make.id) {
        this.filteredModels = [];
        this.modelsPage = 1;
        this.hasMoreModels = true;

        this.loadModels(false);
      } else {
        this.filteredModels = make.models;
        this.cdr.detectChanges();
      }

      setTimeout(() => {
        if (this.dialogBodyRef) {
          this.dialogBodyRef.nativeElement.scrollTop = savedScrollTop;
        }
      }, 0);
    } else if (this.isEdit) {
      this.equipmentForm.patchValue({ make: makeName });
      this.cdr.detectChanges();
      setTimeout(() => {
        if (this.dialogBodyRef) this.dialogBodyRef.nativeElement.scrollTop = savedScrollTop;
      }, 0);
    }
  }

  private loadModels(append: boolean): void {
    if (!this.selectedMake || !this.selectedMake.id) {
      this.filteredModels = [];
      return;
    }

    if (this.modelSubscription) {
      this.modelSubscription.unsubscribe();
    }

    if (append) {
      this.isLoadingMoreModels = true;
    } else {
      this.isLoadingModels = true;
      this.filteredModels = [];
    }

    const selectedBrandId = this.selectedMake.id;

    this.modelSubscription = this.equipmentService
      .getModelsByBrand(selectedBrandId, this.modelsPage, this.modelsPageSize, this.modelSearch)
      .subscribe((response) => {
        if (!this.selectedMake || this.selectedMake.id !== selectedBrandId) {
          return;
        }

        this.filteredModels = append
          ? [...this.filteredModels, ...response.models]
          : response.models;

        this.hasMoreModels = this.modelsPage * this.modelsPageSize < response.total;

        this.isLoadingModels = false;
        this.isLoadingMoreModels = false;
        this.cdr.detectChanges();
      });

    // this.subs.push(this.modelSubscription);
  }

  onModelSearch(value: string): void {
    this.modelSearch = value;

    if (this.modelSearchDebounceTimer) {
      clearTimeout(this.modelSearchDebounceTimer);
    }

    this.modelSearchDebounceTimer = setTimeout(() => {
      this.modelsPage = 1;
      this.hasMoreModels = true;
      this.loadModels(false);
    }, 300);
  }

  //   onModelScroll(event: Event): void {
  //     const el = event.target as HTMLElement;
  //     const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
  //     if (
  //       nearBottom &&
  //       !this.isLoadingMoreModels &&
  //       this.hasMoreModels &&
  //       this.selectedMake &&
  //       this.selectedMake.id
  //     ) {
  //       this.isLoadingMoreModels = true;
  //       this.modelsPage++;
  //       const sub = this.equipmentService
  //         .getModelsByBrand(this.selectedMake.id, this.modelsPage, this.modelsPageSize)
  //         .subscribe((res) => {
  //           this.filteredModels = [...this.filteredModels, ...res.models];
  //           this.hasMoreModels = this.modelsPage * this.modelsPageSize < res.total;
  //           this.isLoadingMoreModels = false;
  //           this.cdr.detectChanges();
  //         });
  //       this.subs.push(sub);
  //     }
  //   }

  onModelScroll(event: Event): void {
    const element = event.target as HTMLElement;

    const nearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 40;

    if (
      nearBottom &&
      !this.isLoadingMoreModels &&
      this.hasMoreModels &&
      this.selectedMake &&
      this.selectedMake.id
    ) {
      this.modelsPage++;
      this.loadModels(true);
    }
  }

  selectModel(model: EquipmentModel): void {
    this.selectedModel = model;
    this.selectedPrefix = null;
    this.prefixSearch = '';
    this.equipmentForm.patchValue({ model: model.model });
    this.loadModelPrefixes(model.id || '');
    this.cdr.detectChanges();
  }

  onPrefixSearch(value: string): void {
    this.prefixSearch = value;
    const search = value.trim().toLowerCase();
    if (!search) {
      this.filteredPrefixesFiltered = [...this.filteredPrefixes];
    } else {
      this.filteredPrefixesFiltered = this.filteredPrefixes.filter((p) => {
        const code = (p.prefix_code || '').toLowerCase();
        const assembly = (p.assembly_type || '').toLowerCase();
        return code.includes(search) || assembly.includes(search);
      });
    }
  }

  selectPrefix(prefix: any): void {
    if (prefix) {
      this.selectedPrefix = prefix;
      this.equipmentForm.patchValue({ equipmentModelPrefixId: prefix.id });
    } else {
      this.selectedPrefix = null;
      this.equipmentForm.patchValue({ equipmentModelPrefixId: '' });
    }
    this.prefixSearch = '';
    this.cdr.detectChanges();
  }

  clearPrefix(): void {
    this.selectedPrefix = null;
    this.prefixSearch = '';
    this.equipmentForm.patchValue({ equipmentModelPrefixId: '' });
    this.cdr.detectChanges();
  }

  loadModelPrefixes(modelId: string): void {
    if (!modelId) {
      this.filteredPrefixes = [];
      this.equipmentForm.patchValue({ equipmentModelPrefixId: '' });
      this.cdr.detectChanges();
      return;
    }

    const sub = this.equipmentService.getModelPrefixes(modelId).subscribe((prefixes) => {
      this.filteredPrefixes = prefixes || [];
      this.filteredPrefixesFiltered = [...this.filteredPrefixes];
      this.selectedPrefix = null;
      this.prefixSearch = '';
      const selectedPrefixId = this.equipmentForm.get('equipmentModelPrefixId').value;
      if (selectedPrefixId && this.filteredPrefixes.some((p) => p.id === selectedPrefixId)) {
        this.selectedPrefix = this.filteredPrefixes.find((p) => p.id === selectedPrefixId);
      } else {
        this.equipmentForm.patchValue({ equipmentModelPrefixId: '' });
      }
      this.cdr.detectChanges();
    });
    this.subs.push(sub);
  }
  selectMast(mast: EquipmentMastOption): void {
    if (this.selectedMast === mast.code) {
      this.selectedMast = '';
    } else {
      this.selectedMast = mast.code;
    }
    this.cdr.detectChanges();
  }
  //   clearMake(): void {
  //     this.selectedMake = null;
  //     this.selectedModel = null;
  //     this.selectedMast = '';
  //     this.filteredModels = [];
  //     this.availableMastOptions = [];
  //     this.equipmentForm.patchValue({ make: '', model: '' });
  //     this.makeSearch = '';
  //     this.filteredMakes = this.allMakes.map((m) => m.name);
  //     this.cdr.detectChanges();
  //   }

  clearMake(): void {
    if (this.modelSubscription) {
      this.modelSubscription.unsubscribe();
      this.modelSubscription = null;
    }

    this.selectedMake = null;
    this.selectedModel = null;
    this.selectedMast = '';

    this.filteredModels = [];
    this.availableMastOptions = [];

    this.equipmentForm.patchValue({
      make: '',
      model: '',
      equipmentModelPrefixId: ''
    });

    this.makeSearch = '';
    this.modelSearch = '';

    this.modelsPage = 1;
    this.hasMoreModels = true;
    this.isLoadingModels = false;
    this.isLoadingMoreModels = false;

    this.filteredMakes = this.allMakes.map((make) => make.name);

    this.cdr.detectChanges();
  }

  clearModel(): void {
    this.selectedModel = null;
    this.filteredPrefixes = [];
    this.filteredPrefixesFiltered = [];
    this.selectedPrefix = null;
    this.prefixSearch = '';
    this.equipmentForm.patchValue({ model: '', equipmentModelPrefixId: '' });
    this.cdr.detectChanges();
  }
  onSubmit(): void {
    if (this.equipmentForm.invalid || !this.selectedModel || this.isSaving) {
      this.equipmentForm.markAllAsTouched();
      return;
    }
    const formVal = this.equipmentForm.value;
    const resolvedFuelType =
      formVal.fuelType === this.FUEL_OTHER
        ? (formVal.fuelTypeOther || '').trim()
        : formVal.fuelType || '';
    const updatedEquipment: Equipment = {
      id: this.isEdit ? this.equipmentId : '',
      ownReference: formVal.ownReference,
      location: formVal.location || '',
      productionMonth: null,
      productionYear: buildProductionDate(formVal.productionMonth, formVal.productionYear),
      make: formVal.make,
      model: this.selectedModel.model,
      serialNumber: (formVal.serialPrefix ? formVal.serialPrefix + '-' : '') + formVal.serialNumber,
      segment: '',
      imageUrl: this.isEdit && this.data && this.data.equipment ? this.data.equipment.imageUrl : '',
      addedDate:
        this.isEdit && this.data && this.data.equipment
          ? this.data.equipment.addedDate
          : new Date(),
      brandId: this.selectedMake
        ? this.selectedMake.id
        : this.isEdit && this.data && this.data.equipment
          ? this.data.equipment.brandId
          : undefined,
      equipmentModelId: this.selectedModel
        ? this.selectedModel.id
        : this.isEdit && this.data && this.data.equipment
          ? this.data.equipment.equipmentModelId
          : undefined,
      equipmentModelPrefixId: formVal.equipmentModelPrefixId || '',
      licensePlate: formVal.licensePlate || '',
      hourMeter: formVal.hourMeter || 0,
      notes: formVal.notes || '',
      chassisSerialNumber: formVal.chassisSerialNumber || '',
      engineBrand: formVal.engineBrand || '',
      engineModel: formVal.engineModel || '',
      engineSerialNumber: formVal.engineSerialNumber || '',
      transmissionBrand: formVal.transmissionBrand || '',
      transmissionModel: formVal.transmissionModel || '',
      transmissionSerialNumber: formVal.transmissionSerialNumber || '',
      axleBrand: formVal.axleBrand || '',
      axleModel: formVal.axleModel || '',
      axleSerialNumber: formVal.axleSerialNumber || '',
      mastType: formVal.mastType || '',
      mastModel: formVal.mastModel || '',
      mastSerialNumber: formVal.mastSerialNumber || '',
      status: formVal.status || 'active',
      origin: formVal.origin || '',
      fuelType: resolvedFuelType,
      capacity: formVal.capacity || '',
      // Tire information
      steeringTireSize: formVal.steeringTireSize || '',
      steeringTireType: formVal.steeringTireType || '',
      loadTireSize: formVal.loadTireSize || '',
      loadTireType: formVal.loadTireType || '',
      spareTireSize: formVal.spareTireSize || '',
      spareTireType: formVal.spareTireType || ''
    };
    this.isSaving = true;
    this.cdr.detectChanges();
    if (this.isEdit) {
      const sub = this.equipmentService
        .updateEquipment(this.equipmentId, updatedEquipment)
        .subscribe(
          (savedEq) => {
            this.uploadPendingImages(savedEq || updatedEquipment);
          },
          (err) => {
            this.isSaving = false;
            this.cdr.detectChanges();
            this.dialogRef.close(updatedEquipment);
          }
        );
      this.subs.push(sub);
    } else {
      const sub = this.equipmentService.addEquipment(updatedEquipment).subscribe(
        (savedEq) => {
          this.uploadPendingImages(savedEq || updatedEquipment);
        },
        (err) => {
          this.isSaving = false;
          this.cdr.detectChanges();
          this.dialogRef.close(updatedEquipment);
        }
      );
      this.subs.push(sub);
    }
  }

  private uploadPendingImages(savedEq: Equipment): void {
    if (this.selectedFiles.length === 0) {
      this.isSaving = false;
      this.dialogRef.close(savedEq);
      return;
    }
    this.isUploadingImages = true;
    this.cdr.detectChanges();
    const sub = this.equipmentService
      .uploadEquipmentImages(savedEq.id, this.selectedFiles)
      .subscribe(
        () => {
          this.isSaving = false;
          this.isUploadingImages = false;
          this.toastr.success(`Đã tải lên ${this.selectedFiles.length} ảnh thành công`);
          this.cdr.detectChanges();
          this.dialogRef.close(savedEq);
        },
        (err) => {
          this.isSaving = false;
          this.isUploadingImages = false;
          console.error('Lỗi tải ảnh lên:', err);
          this.toastr.error('Lưu thiết bị thành công nhưng tải ảnh lên thất bại');
          this.cdr.detectChanges();
          this.dialogRef.close(savedEq);
        }
      );
    this.subs.push(sub);
  }
  onCancel(): void {
    this.dialogRef.close(null);
  }
}
